import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, Clock, MapPin, LogIn, LogOut, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  currentMonthIST,
  formatISTTime,
  monthDaysIST,
  monthLabelIST,
  monthRangeIST,
  todayIST,
} from "../../lib/datetime";
import {
  attendanceLabel,
  count,
  joinParts,
  NO_RECORD,
  NO_RECORD_HELP,
  UNKNOWN_STATUS,
} from "../../lib/vocabulary";
import StickyHeading from "../../components/common/StickyHeading";

/**
 * THE THREE ATTENDANCE SURFACES SHARE THESE. MyAttendancePage, SelfCheckInCard
 * and TeamAttendancePage each carried a verbatim copy of the calendar, the
 * legend, the status class map and the month summary line — which is why the
 * same "Absent" claim had to be fixed in three places and would have drifted in
 * three directions. They live here, exported, and the copies are deleted.
 *
 * A COLOUR IS A CLAIM TOO. Nothing falls through to Present: a status the app
 * has not been taught is drawn in a neutral "not recognised" style and named as
 * such (`UNKNOWN_STATUS`), and a day with NO RECORD is drawn differently again
 * from a day whose record says nothing happened. The two are not the same fact
 * and must not look the same.
 */

/** Server status KEY -> calendar cell colour. The words come from `attendanceLabel`. */
export const CELL_CLS = {
  Present: "bg-emerald-500 text-white",
  "Half Day": "bg-emerald-300 text-emerald-900",
  "On Leave": "bg-amber-400 text-amber-900",
  Holiday: "bg-slate-300 text-slate-700",
  Absent: "bg-red-500 text-white",
  "Not checked in": "bg-slate-400 text-white",
};
/** Never Present green. A status we cannot name is drawn as unnameable. */
const CELL_UNKNOWN = "bg-white text-slate-700 border-2 border-slate-400";
/** Nothing was captured. Dashed, so it is not merely a paler version of a fact. */
const CELL_NO_RECORD = "bg-slate-50 text-callisto-muted border border-dashed border-slate-400";
const CELL_UPCOMING = "bg-white text-callisto-muted border border-callisto";

/** Four characters of the LABEL, never of the server key ("Abs" read as absent). */
const CELL_SHORT = {
  Present: "PRES",
  "Half Day": "HALF",
  "On Leave": "LEAVE",
  Holiday: "HOL",
  Absent: "NO CI",
  "Not checked in": "NO CI",
};

/** Table / card pill colours. Same rule: no fall-through to Present. */
export const STATUS_PILL_CLS = {
  Present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Half Day": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Leave": "bg-amber-50 text-amber-700 border-amber-200",
  Holiday: "bg-slate-100 text-slate-600 border-slate-200",
  Absent: "bg-red-50 text-red-700 border-red-200",
  "Not checked in": "bg-slate-100 text-slate-700 border-slate-200",
};
const PILL_UNKNOWN = "bg-white text-slate-700 border-slate-400 border-dashed";

/** One pill, one status, one set of words. Used by the drill-down and the team cards. */
export function StatusPill({ status, testId }) {
  const known = Object.prototype.hasOwnProperty.call(STATUS_PILL_CLS, status);
  const label = attendanceLabel(status);
  return (
    <span
      data-testid={testId}
      data-status-known={known ? "yes" : "no"}
      title={label === NO_RECORD ? NO_RECORD_HELP : undefined}
      className={`inline-flex items-center text-[11px] font-semibold rounded-full border px-2 py-0.5 ${
        known ? STATUS_PILL_CLS[status] : PILL_UNKNOWN
      }`}
    >
      {label}
    </span>
  );
}

/**
 * "Present 20 · On leave 2 · Not checked in 4 · 76.9% check-in rate".
 *
 * It is a CHECK-IN RATE, not an attendance score: the numerator is check-ins
 * that were captured. When nothing was captured there is nothing to rate, and
 * the line says so rather than printing "0% present".
 */
export function AttendanceMonthSummary({ summary, error, testId = "attendance-month-summary" }) {
  if (error) {
    // A <span>, not a <p>: this renders inside StickyHeading's subtitle <p>.
    return (
      <span className="text-sm text-red-700" data-testid={`${testId}-error`}>
        This month's totals could not be loaded.
      </span>
    );
  }
  if (!summary) return null;
  const counts = summary.counts || {};
  const captured = ["Present", "On Leave", "Holiday", "Absent"].reduce(
    (n, k) => n + (Number(counts[k]) || 0),
    0
  );
  const rate =
    captured && summary.present_pct !== null && summary.present_pct !== undefined
      ? `${summary.present_pct}% check-in rate`
      : "No days captured this month — no rate to report";
  return (
    <span className="text-sm text-callisto-body" data-testid={testId}>
      {joinParts([
        `Present ${count(counts.Present)}`,
        `${attendanceLabel("On Leave")} ${count(counts["On Leave"])}`,
        `${attendanceLabel("Absent")} ${count(counts.Absent)}`,
        `${attendanceLabel("Holiday")} ${count(counts.Holiday)}`,
        rate,
      ])}
    </span>
  );
}

function LegendSwatch({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-3 w-3 rounded-sm ${cls}`} />
      <span className="text-callisto-muted">{label}</span>
    </span>
  );
}

/**
 * The month grid. `byDate` is keyed by IST day.
 *
 * A FAILED LOAD IS NOT AN EMPTY MONTH. With `error` set the grid is not drawn
 * at all — an all-blank calendar is indistinguishable from a month in which
 * nobody checked in, and that was the bug.
 */
export function AttendanceCalendar({ days, byDate, loading, error, testId }) {
  const t = todayIST();
  if (loading) return <Skeleton className="h-56 w-full rounded-xl" />;
  if (error) {
    return (
      <div
        className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
        data-testid={`${testId}-error`}
      >
        This month's attendance could not be loaded, so no days are shown. {error}
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-7 gap-1.5" data-testid={testId}>
        {days.map((d) => {
          const r = byDate[d.iso];
          const isFuture = d.iso > t;
          const known = r && Object.prototype.hasOwnProperty.call(CELL_CLS, r.status);
          const label = r ? attendanceLabel(r.status) : isFuture ? "Upcoming" : NO_RECORD;
          const cls = r
            ? known
              ? CELL_CLS[r.status]
              : CELL_UNKNOWN
            : isFuture
            ? CELL_UPCOMING
            : CELL_NO_RECORD;
          return (
            <div
              key={d.iso}
              data-testid={`${testId}-day-${d.iso}`}
              data-record={r ? "reported" : isFuture ? "upcoming" : "none"}
              title={r ? label : isFuture ? "Upcoming" : `${NO_RECORD} — ${NO_RECORD_HELP}`}
              aria-label={`${d.iso}: ${label}`}
              className={`aspect-square rounded-md flex flex-col items-center justify-center text-sm font-bold ${cls}`}
            >
              <span>{d.day}</span>
              {r && (
                <span className="text-[8px] uppercase opacity-80">
                  {known ? CELL_SHORT[r.status] : "?"}
                </span>
              )}
              {!r && !isFuture && <span className="text-[8px] uppercase opacity-80">·</span>}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px]" data-testid={`${testId}-legend`}>
        <LegendSwatch cls={CELL_CLS.Present} label={attendanceLabel("Present")} />
        <LegendSwatch cls={CELL_CLS["On Leave"]} label={attendanceLabel("On Leave")} />
        <LegendSwatch cls={CELL_CLS.Absent} label={attendanceLabel("Absent")} />
        <LegendSwatch cls={CELL_CLS.Holiday} label={attendanceLabel("Holiday")} />
        <LegendSwatch cls={CELL_NO_RECORD} label={NO_RECORD} />
        <LegendSwatch cls={CELL_UNKNOWN} label={UNKNOWN_STATUS} />
      </div>
      <p className="mt-1.5 text-[11px] text-callisto-muted" data-testid={`${testId}-no-record-help`}>
        {NO_RECORD_HELP}
      </p>
    </>
  );
}

export default function MyAttendancePage() {
  const start = currentMonthIST();
  const [year, setYear] = useState(start.year);
  const [month, setMonth] = useState(start.month);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayRec, setTodayRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { first, last } = monthRangeIST(year, month);
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/attendance", { params: { from_date: first, to_date: last } }),
        api.get("/attendance/summary", { params: { year, month } }),
      ]);
      setRows(list || []);
      setSummary(sum);
      const t = todayIST();
      setTodayRec((list || []).find((r) => r.date === t) || null);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  function captureGps() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) return resolve(null);
      const timer = setTimeout(() => resolve(null), 4000);
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => { clearTimeout(timer); resolve(null); },
        { timeout: 3500 }
      );
    });
  }

  async function doCheckIn() {
    setActing(true);
    try {
      const gps = await captureGps();
      const { data } = await api.post("/attendance/checkin", { gps_location: gps });
      setTodayRec(data);
      toast.success(data.status === "On Leave" ? "Marked on leave today" : "Checked in");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  async function doCheckOut() {
    setActing(true);
    try {
      const gps = await captureGps();
      const { data } = await api.put("/attendance/checkout", { gps_location: gps });
      setTodayRec(data);
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  const byDate = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.date] = r;
    return m;
  }, [rows]);

  const { days } = useMemo(() => monthDaysIST(year, month), [year, month]);

  function shift(delta) {
    let nm = month + delta, ny = year;
    if (nm < 1) { nm = 12; ny -= 1; }
    else if (nm > 12) { nm = 1; ny += 1; }
    setYear(ny); setMonth(nm);
  }

  return (
    <div className="space-y-6" data-testid="my-attendance-page">
      <StickyHeading
        icon={Sparkles}
        label="My Attendance"
        title={monthLabelIST(year, month)}
        subtitle={<AttendanceMonthSummary summary={summary} error={error} testId="my-attendance-summary" />}
        testId="my-attendance-heading"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => shift(-1)} className="h-10 w-10 p-0 border-callisto"><ChevronLeft size={16} /></Button>
            <Button variant="outline" onClick={() => shift(1)} className="h-10 w-10 p-0 border-callisto"><ChevronRight size={16} /></Button>
          </div>
        }
      />

      {/* Check-in card */}
      <div className="bg-white border border-callisto rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="my-attendance-checkin-card">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">Today</p>
          {todayRec ? (
            <div className="mt-1">
              <p className="text-lg font-bold text-callisto-navy flex items-center gap-2">
                Status: <StatusPill status={todayRec.status} testId="my-attendance-today-status" />
              </p>
              {todayRec.check_in_time && (
                <p className="text-xs text-callisto-muted mt-0.5 flex items-center gap-1.5">
                  <Clock size={12} /> in {formatISTTime(todayRec.check_in_time)}{" "}
                  {todayRec.check_out_time && <>· out {formatISTTime(todayRec.check_out_time)} · {todayRec.working_hours}h</>}
                </p>
              )}
              {todayRec.check_in_area && (
                <p className="text-xs text-callisto-muted mt-0.5 flex items-center gap-1.5">
                  <MapPin size={12} /> {todayRec.check_in_area}
                </p>
              )}
            </div>
          ) : (
            <p className="mt-1 text-callisto-body" data-testid="my-attendance-today-none">
              No check-in captured for you today yet.
            </p>
          )}
        </div>
        <div>
          {!todayRec ? (
            <Button
              onClick={doCheckIn}
              disabled={acting}
              className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-md"
              data-testid="attendance-checkin-button"
            >
              {acting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5 mr-2" /> Check In</>}
            </Button>
          ) : todayRec.status === "Present" && !todayRec.check_out_time ? (
            <Button
              onClick={doCheckOut}
              disabled={acting}
              className="h-12 px-6 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-bold text-base shadow-md"
              data-testid="attendance-checkout-button"
            >
              {acting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogOut className="h-5 w-5 mr-2" /> Check Out</>}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Calendar — owns its own error state, so a failed load never renders as
          a month in which nothing happened. */}
      <AttendanceCalendar
        days={days}
        byDate={byDate}
        loading={loading}
        error={error}
        testId="my-attendance-calendar"
      />
    </div>
  );
}
